import { FormikProps } from "formik"
import React, { forwardRef, Suspense, useRef } from "react"
import { MERCHANT_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import {
    fetchSupplierMerchantDetails,
    SupplierMerchantDetailsResponse,
    updateMerchant,
} from "../../../../../supplier/admin/merchant/merchants/supplierMerchantsApi"
import { UpdateMerchantForm } from "../components/UpdateMerchantForm"
import {
    CreateUpdateMerchant,
    CreateUpdateMerchantFormValues,
    CurrencyCode,
} from "../../../../../supplier/admin/merchant/merchants/supplierMerchantsTypes"
import { defaultMerchantInitialValues } from "../constants"
import { UserType } from "../../../../../login/loginTypes"
import { MerchantAccountQuery } from "../merchantAccountApi"

export function UpdateMerchantAccount() {
    const merchantFormRef = useRef<FormikProps<CreateUpdateMerchantFormValues>>(null)

    const handleSaveClick = () => {
        merchantFormRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="merchant-update-account-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_UPDATE_ACCOUNT}
                    title="Merchant account"
                    rightElement={
                        <LocalizedStrict id="merchant-update-account-save-button">
                            <StandardButton onClick={handleSaveClick}>Save</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-account">
                            <Breadcrumb>Account</Breadcrumb>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-merchant-account">
                            <Breadcrumb>Merchant account</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Merchant ref={merchantFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

function transformData({ merchant }: SupplierMerchantDetailsResponse): CreateUpdateMerchantFormValues {
    const acquiringDetails = merchant.acquiringDetails
    let acquiringDetailsValue = {}
    if (acquiringDetails && "acquiring" in acquiringDetails) {
        acquiringDetailsValue = {
            acquiring: {
                type: acquiringDetails.acquiring.type,
                salesChannels: acquiringDetails.acquiring.salesChannel,
                defaultCurrencyCode: acquiringDetails.acquiring.defaultCurrencyCode,
                bankAccount: {
                    refId: acquiringDetails.acquiring.bankAccount.refId,
                },
                industryCodes: acquiringDetails.acquiring.industryCodes.map((item) => item.refId),
            },
        }
    }

    return {
        caseNumber: merchant.caseNumber,
        name: merchant.name,
        address: merchant.address,
        contactDetails: merchant.contactDetails,
        acquiringDetails: acquiringDetailsValue || {},
        openingHours: merchant?.openingHours,
        isActive: merchant.isActive,
    }
}

const Merchant = forwardRef<FormikProps<CreateUpdateMerchantFormValues>>((_, ref) => {
    const { merchantId } = useMerchant()

    const merchantDetails = useApiQuery(fetchSupplierMerchantDetails, {
        queryName: MerchantAccountQuery.DETAILS,
        dependencies: {
            merchantId,
        },
        suspense: true,
        useErrorBoundary: true,
        select: transformData,
    })

    const { mutate: callUpdateMerchant } = useApiMutation(updateMerchant, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantAccountQuery.DETAILS] })
        },
    })

    const editMerchant = (data: CreateUpdateMerchantFormValues) => {
        const merchant: CreateUpdateMerchant = {
            type: UserType.MERCHANT,
            caseNumber: merchantDetails.data?.caseNumber,
            name: data.name,
            address: data.address,
            contactDetails: data.contactDetails,
            isActive: data.isActive,
            openingHours: data.openingHours,
            acquiringDetails: {
                ...merchantDetails.data?.acquiringDetails,
                acquiring: {
                    defaultCurrencyCode: CurrencyCode.DKK,
                    type: merchantDetails.data?.acquiringDetails?.acquiring?.type || "",
                    salesChannels: merchantDetails.data?.acquiringDetails?.acquiring?.salesChannels
                        ? [merchantDetails.data?.acquiringDetails?.acquiring?.salesChannels]
                        : [],
                },
            },
        }

        callUpdateMerchant({ merchantId: merchantId.toString(), merchant: merchant })
    }

    return (
        <UpdateMerchantForm
            ref={ref}
            initialValues={merchantDetails.data ?? defaultMerchantInitialValues}
            onSubmit={editMerchant}
        />
    )
})
